/**
 * Shared locale/currency helpers for non-React contexts (e.g. axios interceptors,
 * exporters, or any place a hook cannot be used).
 *
 * When inside a React component, PREFER `useCurrency()` from
 * `../contexts/CurrencyContext`. This module exists to keep the last-known
 * settings globally accessible for fire-and-forget utilities.
 */

let cache = {
  code: null,
  locale: 'en-US',
  decimals: 2,
  symbol: null,
  timezone: 'America/New_York',
  dateFormat: 'MM/DD/YYYY',
};

/** Called by CurrencyContext on every settings refresh. */
export function updateLocaleCache(next) {
  cache = { ...cache, ...next };
}

export function getCurrencyCode() { return cache.code; }
export function getLocale() { return cache.locale; }
export function getTimezone() { return cache.timezone; }

/** Currency format usable in non-React contexts. Returns '—' if no code set. */
export function formatCurrency(amount) {
  const n = Number(amount || 0);
  if (!cache.code) return '—';
  try {
    return new Intl.NumberFormat(cache.locale, {
      style: 'currency',
      currency: cache.code,
      minimumFractionDigits: cache.decimals,
      maximumFractionDigits: cache.decimals,
    }).format(n);
  } catch {
    return `${cache.symbol || ''}${n.toFixed(cache.decimals)}`;
  }
}

export function formatDate(d) {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(cache.locale, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      timeZone: cache.timezone,
    }).format(dt);
  } catch {
    return dt.toLocaleDateString();
  }
}

export function formatDateTime(d) {
  if (!d) return '';
  const dt = d instanceof Date ? d : new Date(d);
  if (isNaN(dt.getTime())) return '';
  try {
    return new Intl.DateTimeFormat(cache.locale, {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
      timeZone: cache.timezone,
    }).format(dt);
  } catch {
    return dt.toLocaleString();
  }
}
